import { FC, useCallback, useEffect, useState } from 'react'
import { Button, FormItem, Input, Option, Rack, RackButton, RackSelect, Radio } from '@cargomart/ui'
import IconAlert from 'assets/alert-fill.svg?react'
import cn from 'classnames'
import { LinkButton } from 'components/extend/Link'
import { PlaceholderCell } from 'components/extend/PlaceholderCell'
import ModalItem from 'components/ModalItem'
import { MODAL_IDS, useModal } from 'components/ModalProvider'
import { ModalSimple } from 'components/ModalSimple'
import { FieldTextarea } from 'forms/FieldTextarea'
import { maxChar } from 'forms/validators'
import { SelectAddress } from 'modules/customer/components/SelectAddress/SelectAddress'
import { DeliveryTypeEnum } from 'modules/deliveryInfo/types'
import { ModalTerminal } from 'modules/order/components/ModalTerminal/ModalTerminal'
import { formattedCoordinates } from 'modules/order/helpers'
import IconArrowBack from 'remixicon/icons/Arrows/arrow-go-back-line.svg?react'
import IconMap from 'remixicon/icons/Map/map-pin-line.svg?react'
import IconSearch from 'remixicon/icons/System/search-line.svg?react'
import { CourierDeliveryInfo } from 'schema'

import { OrderCreateFormNoDadataRouteItem } from '../OrderCreateFormNoDadataRouteItem/OrderCreateFormNoDadataRouteItem'
import { OrderCreateFormRouteItemProps } from './types'

import s from './OrderCreateFormRouteItem.module.less'

export const OrderCreateFormRouteItem: FC<OrderCreateFormRouteItemProps> = (props) => {
  const {
    name,
    pointType,
    errorAddress,
    setErrorAddress,
    address,
    isAddressLoading,
    searchAddressValue,
    setSearchAddressValue,
    daDataSearchAddressValue,
    setDaDataSearchAddressValue,
    debouncedGetAddress,
    setSelectedOption,
    isAddressCourierDelivery,
    isAddressTerminalDelivery,
    isDeliveryTypeLoading,
    selectedOption,
    isCourier,
    setIsCourier,
    terminals,
    selectTerminal,
    handleSelectTerminal,
    setComment,
    error,
    deliveryTypeError,
    terminalListError,
    isTerminalLoading,
    onChange,
    isDadataDisable,
    setIsDadataDisable,
    customerTypeValue,
    customerId,
    setTerminalId,
    setPointType,
    setDepartureTerminalSchedule,
    initialValue
  } = props
  const { openModal, closeModal } = useModal()
  const modalId =
    pointType === CourierDeliveryInfo.pointType.DEPARTURE
      ? MODAL_IDS.OrderCreateTerminalDeparture
      : MODAL_IDS.OrderCreateTerminalDestination
  const orderCreateNoDadataRouteModalId =
    pointType === CourierDeliveryInfo.pointType.DEPARTURE
      ? MODAL_IDS.OrderCreateNoDadataRouteDeparture
      : MODAL_IDS.OrderCreateNoDadataRouteDestination

  useEffect(() => {
    if (!initialValue) {
      handleSelectTerminal(null)
    }
  }, [selectedOption]) // eslint-disable-line

  useEffect(() => {
    if (isDadataDisable) {
      handleSelectTerminal(null)
      setIsCourier(undefined)
    }
  }, [isDadataDisable]) // eslint-disable-line

  useEffect(() => {
    if (!initialValue && !terminals?.find((terminal) => terminal.id === selectTerminal?.id)) {
      handleSelectTerminal(null)
    }
  }, [terminals, customerTypeValue, customerId]) // eslint-disable-line

  const MOSCOW_CENTER_BY_COORDINATES = [55.76, 37.62]
  const MOSCOW_CENTER_BY_ADDRESS = 'Москва, Театральный проезд, 1с1'

  const [isErrorAddress, setIsErrorAddress] = useState(false)
  const [noDadataAddress, setNoDadataAddress] = useState('')
  const [coords, setCoords] = useState(MOSCOW_CENTER_BY_COORDINATES)
  const [mapAddress, setMapAddress] = useState(MOSCOW_CENTER_BY_ADDRESS)

  useEffect(() => {
    setNoDadataAddress(daDataSearchAddressValue)
  }, [daDataSearchAddressValue])

  const terminalCorrectCoordinates = {
    latitude: selectTerminal?.latitude === selectedOption?.latitude,
    longitude: selectTerminal?.longitude === selectedOption?.longitude
  }

  const terminal = selectTerminal || undefined

  const handleOpenModalTerminal = useCallback(() => {
    openModal(modalId)
  }, [modalId, openModal])

  const isCourierDeliveryNotAvailable = !isAddressCourierDelivery && isAddressTerminalDelivery
  const isOnlyCourierDeliveryAvailable = isAddressCourierDelivery && !isAddressTerminalDelivery

  const warning = (): string | undefined => {
    if (isCourierDeliveryNotAvailable) {
      return 'Для указанного адреса курьерская доставка недоступна'
    }
    if (isOnlyCourierDeliveryAvailable) {
      return 'Для этого адреса доступна только курьерская доставка'
    }
  }

  useEffect(() => {
    // @ts-ignore
    if (deliveryTypeError?.data?.message) {
      // @ts-ignore
      deliveryTypeError?.data?.message
        .filter((el: any) => el.path.includes(`${pointType}.point.addressDetail.address`))
        // eslint-disable-next-line array-callback-return
        .map((el: any) => {
          setErrorAddress(el.title)
        })
      // @ts-ignore
    } else if (terminalListError?.data?.message) {
      // @ts-ignore
      terminalListError?.data?.message
        .filter((el: any) => el.path.includes(`${pointType}.point.addressDetail.address`))
        // eslint-disable-next-line array-callback-return
        .map((el: any) => {
          setErrorAddress(el.title)
        })
    } else if (!daDataSearchAddressValue?.length && error) {
      setErrorAddress(error)
      // @ts-ignore
    } else if (isCourier && selectedOption?.fiasLevel < 7) {
      setErrorAddress('Уточните адрес')
    } else {
      setErrorAddress('')
    }
    // eslint-disable-next-line
  }, [
    // @ts-ignore
    deliveryTypeError?.data?.message,
    // @ts-ignore
    terminalListError?.data?.message,
    error,
    // @ts-ignore
    selectedOption?.fiasLevel
  ])

  useEffect(() => {
    if (isCourierDeliveryNotAvailable || isOnlyCourierDeliveryAvailable) {
      setIsCourier(undefined)
    }
  }, [isAddressCourierDelivery, isAddressTerminalDelivery]) // eslint-disable-line

  useEffect(() => {
    if (address !== null) {
      setIsDadataDisable(false)
    } else {
      setIsDadataDisable(true)
    }
  }, [address]) // eslint-disable-line

  const handleCloseModal = useCallback(() => {
    closeModal(orderCreateNoDadataRouteModalId)
  }, [closeModal, orderCreateNoDadataRouteModalId])

  const handleAccept = useCallback(
    ({ address, coords }: { address: string; coords: number[] }) => {
      setNoDadataAddress(address)
      setSelectedOption({
        latitude: coords[0].toFixed(7),
        longitude: coords[1].toFixed(7),
        address
      })
      setErrorAddress('')
      handleCloseModal()
    },
    [handleCloseModal, setErrorAddress, setSelectedOption]
  )

  const handleOpenModal = useCallback(() => {
    openModal(orderCreateNoDadataRouteModalId)
  }, [openModal, orderCreateNoDadataRouteModalId])

  return (
    <div className={s.body}>
      <ModalItem id={orderCreateNoDadataRouteModalId}>
        <OrderCreateFormNoDadataRouteItem
          address={mapAddress}
          coords={coords}
          handleAccept={handleAccept}
          handleCloseModal={handleCloseModal}
          id={orderCreateNoDadataRouteModalId}
          setAddress={setMapAddress}
          setCoords={setCoords}
        />
      </ModalItem>
      <ModalItem id={modalId}>
        <ModalSimple id={modalId} onClose={() => closeModal(modalId)} title={terminal?.name || ''}>
          <ModalTerminal terminal={terminal} />
        </ModalSimple>
      </ModalItem>

      <div className={s.addressBlock}>
        {!isDadataDisable ? (
          <div className={s.select}>
            <SelectAddress
              address={address}
              daDataSearchAddressValue={daDataSearchAddressValue}
              debouncedGetAddress={debouncedGetAddress}
              errorAddress={errorAddress}
              initialValue={initialValue?.addressDetail?.address}
              isAddressLoading={isAddressLoading}
              isCourier={isCourier}
              isErrorAddress={isErrorAddress}
              name={name}
              placeholder="Населённый пункт или точный адрес"
              searchAddressValue={searchAddressValue}
              setDaDataSearchAddressValue={setDaDataSearchAddressValue}
              setErrorAddress={setErrorAddress}
              setIsErrorAddress={setIsErrorAddress}
              setSearchAddressValue={setSearchAddressValue}
              setSelectedOption={setSelectedOption}
            />
            {!isDeliveryTypeLoading && <div className="text-body-sm text-attention">{warning()}</div>}
          </div>
        ) : (
          <>
            <div className="w-full">
              <Input disabled className="mb-1 w-full" value={noDadataAddress} />
              {errorAddress ? (
                <span className={s.error}>{errorAddress}</span>
              ) : (
                <div className="text-body-sm text-attention">
                  {' '}
                  Сервис поиска адреса не работает. Пожалуйста, укажите адрес на карте
                </div>
              )}
            </div>
            <Button
              onClick={handleOpenModal}
              prefix={<IconMap containerSize="nm" size="xs" />}
              size="nm"
              variant="outline"
            >
              На карте
            </Button>
          </>
        )}
      </div>
      <div className={s.radioBtns}>
        <Radio
          checked={isCourier}
          className={cn(s.radioBtn, error && selectedOption && isCourier === undefined ? s.radioBtn__error : '')}
          disabled={!isAddressCourierDelivery}
          invalid={!!(error && selectedOption && isCourier === undefined)}
          name={pointType === CourierDeliveryInfo.pointType.DEPARTURE ? 'departureCourier' : 'destinationCourier'}
          onChange={() => {
            setIsCourier(true)
            setPointType && setPointType(DeliveryTypeEnum.courier)
            // @ts-ignore
            if (selectedOption?.fiasLevel < 7) {
              setErrorAddress('Уточните адрес')
            }
            setTerminalId && setTerminalId('')
            handleSelectTerminal(null)
            onChange(true)
          }}
        >
          {pointType === CourierDeliveryInfo.pointType.DEPARTURE
            ? 'Нужен курьер для забора груза'
            : 'Доставьте курьером по адресу'}
        </Radio>
        <Radio
          checked={isCourier === false}
          className={cn(s.radioBtn, error && selectedOption && isCourier === undefined ? s.radioBtn__error : '')}
          disabled={!isAddressTerminalDelivery}
          invalid={!!(error && selectedOption && isCourier === undefined)}
          name={pointType === CourierDeliveryInfo.pointType.DEPARTURE ? 'departureTerminal' : 'destinationTerminal'}
          onChange={() => {
            setIsCourier(false)
            setPointType && setPointType(DeliveryTypeEnum.terminal)
            setErrorAddress('')
            handleSelectTerminal(null)
            setTerminalId && setTerminalId('')
            onChange(false)
          }}
        >
          {pointType === CourierDeliveryInfo.pointType.DEPARTURE
            ? 'Самостоятельная передача груза на терминале'
            : 'Самостоятельное получение груза на терминале'}
        </Radio>
        {isCourier && (
          <>
            <div className={s.isCourierBlock}>
              <div className={s.isCourierLabel}>Комментарий к адресу</div>
              <FormItem>
                <FieldTextarea
                  className={s.isCourierInput}
                  maxRows={7}
                  minRows={2}
                  // TODO обрабатывать через форму
                  onChange={(value) => setComment(value)}
                  validate={maxChar(1000)}
                  name={`${
                    pointType === CourierDeliveryInfo.pointType.DEPARTURE ? 'departurePoint' : 'destinationPoint'
                  }.addressDetail.comment`}
                />
              </FormItem>
              <div className={s.hint}>Необязательно</div>
            </div>
            {terminalCorrectCoordinates.latitude && terminalCorrectCoordinates.longitude ? (
              <div className="flex gap-4 rounded bg-attention-container p-3">
                <IconAlert className="text-on-negative-container" containerSize="nm" size="xs" />
                <div>
                  <h2 className="!text-label-lg !font-medium text-on-surface">
                    Выбранный адрес совпадает с адресом нашего терминала
                  </h2>
                  <div className="text-on-surface">
                    {pointType === CourierDeliveryInfo.pointType.DEPARTURE
                      ? 'Выберите вариант «Самостоятельная передача груза на терминале», если забор курьером не требуется.'
                      : 'Выберите вариант «Самостоятельное получение груза на терминале», если доставка курьером не требуется.'}
                  </div>
                </div>
              </div>
            ) : (
              ''
            )}
          </>
        )}
        {isCourier === false && isAddressTerminalDelivery && (
          <div className={s.isTerminalBlock}>
            {!isTerminalLoading ? (
              <>
                <Rack
                  error={selectTerminal?.name ? undefined : error}
                  label={
                    <h2 className="!text-label-lg !font-medium text-on-surface">
                      {selectTerminal?.name ? selectTerminal?.name : 'Терминал'}
                    </h2>
                  }
                  onChange={(v: any) => {
                    handleSelectTerminal(v)
                    setTerminalId && setTerminalId(v.id)
                    setDepartureTerminalSchedule && setDepartureTerminalSchedule(v.schedule)

                    // @cargomart/ui при закрытии списка возвращает фокус на элемент, который был
                    // сфокусирован до открытия. Им оказывается поле даты, из-за чего DatePicker
                    // открывает календарь. Гасим этот фокус на следующем кадре. TODO убрать после фикса в @cargomart/ui
                    requestAnimationFrame(() => {
                      const active = document.activeElement

                      if (active instanceof HTMLInputElement && active.name === 'departureDate') {
                        active.blur()
                      }
                    })
                  }}
                >
                  <div>
                    {selectTerminal?.name ? (
                      <div className="flex flex-col gap-2 px-3 py-2">
                        <div className="flex-col">
                          <div className="text-on-surface">{selectTerminal?.address}</div>
                          <div className="text-on-surface-variant">
                            {selectTerminal?.latitude && selectTerminal?.longitude
                              ? formattedCoordinates(
                                  [Number(selectTerminal?.latitude), Number(selectTerminal?.longitude)],
                                  true
                                )
                              : ''}
                          </div>
                        </div>
                        <LinkButton className="!text-on-surface-link" onClick={handleOpenModalTerminal}>
                          График работы и адрес на карте
                        </LinkButton>
                      </div>
                    ) : (
                      ''
                    )}
                    <RackButton
                      label={selectTerminal?.name ? 'Изменить терминал' : 'Выбрать'}
                      prefix={
                        selectTerminal?.name ? (
                          <IconArrowBack containerSize="nm" size="xs" />
                        ) : (
                          <IconSearch containerSize="nm" size="xs" />
                        )
                      }
                    >
                      <RackSelect>
                        {terminals?.length ? (
                          terminals?.map((option) => (
                            <Option
                              key={option?.id}
                              className={s.terminalOption}
                              postfix={<>{option?.address}</>}
                              title={option?.name}
                              value={option}
                            />
                          ))
                        ) : (
                          <div className="p-3">Не найдено терминалов для выбранного населенного пункта</div>
                        )}
                      </RackSelect>
                    </RackButton>
                  </div>
                </Rack>
              </>
            ) : (
              <PlaceholderCell height={86} />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
